/*
 * Copyright (c) 2024 - 2026. Bluemate B.V.
 */

import './style/bluemateDialog.scss';

import {CloseButton, OrnamentShape, WithBackground} from '@dvrd/dvr-controls';

import React, {ReactNode, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import BluemateButton from '../button/bluemateButton';

import classNames from 'classnames';

import {SnoozeConfig} from '../../../util/snoozeUtil';

import {useSnooze} from '../../../hooks/useSnooze';

export type DialogBackdropVariant = 'full' | 'content';

export interface DialogSnoozeConfig extends SnoozeConfig {
    /** Confirm clears the dismissal counter so the ladder starts over (default true). */
    resetOnConfirm?: boolean;
    /** When false, only the footer cancel records a dismissal — the header X,
     *  backdrop and Escape close without recording (default true). */
    dismissOnClose?: boolean;
}

interface BluemateDialogProps {
    active: boolean;
    onClose: VoidFunction;
    title: string;
    /** Optional muted line under the title (e.g. a stepper's "Stap 1 van 2"). */
    subtitle?: string;
    children: ReactNode;
    /** Custom footer content, rendered in the pinned footer band instead of the
     *  default cancel/confirm pair. Use it for wizard-style actions — anything
     *  placed in `children` lives inside the SCROLLING body and travels with the
     *  content. */
    footer?: ReactNode;
    onConfirm?: VoidFunction;
    confirmLabel?: ReactNode;
    cancelLabel?: ReactNode;
    /** Overrides the footer cancel button's action (default: onClose) — e.g.
     *  a "clear filters" button that should not dismiss the dialog. */
    onCancel?: VoidFunction;
    /** Look of the footer cancel button. 'secondary' (default) is the pale
     *  blue fill; 'tertiary' is the white face + hairline border used by the
     *  notification dialogs. */
    cancelVariant?: 'secondary' | 'tertiary';
    confirmOrnaments?: OrnamentShape | OrnamentShape[];
    cancelOrnaments?: OrnamentShape | OrnamentShape[];
    confirmDisabled?: boolean;
    loading?: boolean;
    width?: string;
    className?: string;
    hideCancel?: boolean;
    /** Forwarded to WithBackground: when true the dialog does not close on a
     *  click outside it (use for dialogs holding unsaved input). */
    preventBackgroundClick?: boolean;
    /** Forwarded to WithBackground: when true the Escape key does not close it. */
    escapeDisabled?: boolean;
    // Hide the header close (X) button, e.g. for a non-dismissable dialog.
    hideClose?: boolean;
    /** Rendered in the header's top-right, left of the close (X) — for an
     *  action that belongs to the dialog as a whole rather than to its footer
     *  (e.g. the wizard's "Premium toevoegen..." upsell). */
    headerAction?: ReactNode;
    /** Rendered as a direct child of the dialog root, on top of header, body
     *  AND footer. Content placed in `children` cannot do this: the body is a
     *  positioned scroll container, so an absolutely-positioned sheet inside it
     *  only covers (and scrolls with) the body band. */
    overlay?: ReactNode;
    // 'full' (default) dims the whole viewport like a normal modal. 'content' only dims the page
    // content section, leaving the header and sidebar menu uncovered and interactive.
    backdropVariant?: DialogBackdropVariant;
    /** Persistent "ask again later" behaviour for nag-style dialogs: every close
     *  without confirm (X, backdrop, Escape, footer cancel) records a dismissal
     *  (limit to the footer cancel via `dismissOnClose: false`), and while the
     *  resulting cooldown runs the dialog renders nothing even when `active`.
     *  Do not combine with a custom `onCancel` that keeps the dialog open —
     *  cancel counts as a dismissal here. */
    snooze?: DialogSnoozeConfig;
}

interface ContentBackgroundProps {
    active: boolean;
    onClose: VoidFunction;
    zIndex?: number;
    // Element the backdrop is clipped to. Defaults to the page content column.
    targetSelector?: string;
    children: ReactNode;
}

export interface Box {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface BackdropGeometry {
    // What the backdrop covers: the whole content column, including the part off screen.
    backdrop: Box;
    // What the dialog is centered on: the part of that column actually on screen.
    anchor: Box;
}

const sameBox = (a: Box, b: Box): boolean =>
    a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

/**
 * Views without a mobile layout keep a min-width buffer, so the content column runs far past the
 * right edge of a phone screen. Centering the dialog on that column pushes it (half) off screen, so
 * the dialog is centered on the column's VISIBLE part instead — the screen on such a view, the
 * column itself (unchanged) on a view that fits. The backdrop still covers the whole column.
 */
export function computeBackdropGeometry(
    rect: DOMRect,
    viewportWidth: number,
    viewportHeight: number
): BackdropGeometry {
    const backdrop = {top: rect.top, left: rect.left, width: rect.width, height: rect.height};
    const left = Math.max(rect.left, 0);
    const top = Math.max(rect.top, 0);
    const width = Math.min(rect.right, viewportWidth) - left;
    const height = Math.min(rect.bottom, viewportHeight) - top;
    const anchor =
        width > 0 && height > 0
            ? {top, left, width, height}
            : {top: 0, left: 0, width: viewportWidth, height: viewportHeight};
    return {backdrop, anchor};
}

/**
 * Backdrop variant that only covers the page content section (the `.page` column), leaving the
 * header and sidebar menu uncovered and fully clickable. It tracks the target's size so it stays
 * aligned when the sidebar collapses or the window resizes. Kept below the chrome z-index so the
 * menu and header remain interactive even where the backdrop reaches them.
 */
function ContentBackground(props: ContentBackgroundProps) {
    const {active, onClose, zIndex = 1100, targetSelector = '.page-content > .page', children} = props;
    const [geometry, setGeometry] = useState<BackdropGeometry | null>(null);

    useEffect(() => {
        if (!active) return;
        const target = document.querySelector(targetSelector) ?? document.querySelector('.page');
        if (!target) return;
        const update = () => {
            const next = computeBackdropGeometry(target.getBoundingClientRect(), window.innerWidth, window.innerHeight);
            // Skip the state update (and re-render) when the geometry hasn't actually changed.
            setGeometry(prev =>
                prev && sameBox(prev.backdrop, next.backdrop) && sameBox(prev.anchor, next.anchor) ? prev : next
            );
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(target);
        window.addEventListener('resize', update);
        // A scroll shifts the target without resizing it, so neither observer above fires.
        window.addEventListener('scroll', update, true);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [active, targetSelector]);

    if (!active || !geometry) return null;

    function onMouseDown(evt: React.MouseEvent) {
        if (evt.target === evt.currentTarget) onClose();
    }

    return (
        <div className='content-dialog-background' style={{...geometry.backdrop, zIndex}} onMouseDown={onMouseDown}>
            <div className='content-dialog-anchor' style={geometry.anchor}>
                {children}
            </div>
        </div>
    );
}

export default function BluemateDialog(props: BluemateDialogProps) {
    const {t} = useTranslation();
    const {
        active,
        onClose,
        title,
        subtitle,
        children,
        footer,
        onConfirm,
        confirmLabel = t('controls.dialog.confirm', 'Goedkeuren'),
        cancelLabel = t('controls.dialog.cancel', 'Annuleren'),
        onCancel,
        cancelVariant = 'secondary',
        confirmOrnaments,
        cancelOrnaments,
        confirmDisabled,
        loading,
        width,
        className,
        hideCancel,
        preventBackgroundClick,
        escapeDisabled,
        hideClose,
        headerAction,
        overlay,
        backdropVariant = 'full',
        snooze
    } = props;

    const {suppressed, dismiss, reset} = useSnooze(snooze);

    if (snooze && suppressed) return null;

    function onDismissingClose() {
        if (snooze && (snooze.dismissOnClose ?? true)) dismiss();
        onClose();
    }

    function onDismissingCancel() {
        if (snooze) dismiss();
        (onCancel ?? onClose)();
    }

    function onResettingConfirm() {
        if (snooze && (snooze.resetOnConfirm ?? true)) reset();
        onConfirm?.();
    }

    const dialog = (
        <div className={classNames('bluemate-dialog', 'default-popup', className)} style={width ? {width} : undefined}>
            <div className='header-container'>
                <div className='header-titles'>
                    <h3>{title}</h3>
                    {subtitle && <span className='header-subtitle'>{subtitle}</span>}
                </div>
                <div className='header-actions'>
                    {headerAction}
                    {!hideClose && <CloseButton onClick={onDismissingClose} />}
                </div>
            </div>
            <div className='body-container'>{children}</div>
            {!!footer && <div className='footer-container'>{footer}</div>}
            {!footer && (!hideCancel || onConfirm) && (
                <div className='footer-container'>
                    {!hideCancel && (
                        <BluemateButton
                            label={cancelLabel}
                            secondary={cancelVariant === 'secondary'}
                            tertiary={cancelVariant === 'tertiary'}
                            ornaments={cancelOrnaments}
                            onClick={onDismissingCancel}
                            disabled={loading}
                        />
                    )}
                    {onConfirm && (
                        <BluemateButton
                            label={confirmLabel}
                            ornaments={confirmOrnaments}
                            onClick={onResettingConfirm}
                            disabled={confirmDisabled || loading}
                        />
                    )}
                </div>
            )}
            {overlay}
        </div>
    );

    if (backdropVariant === 'content')
        return (
            <ContentBackground active={active} onClose={onDismissingClose}>
                {dialog}
            </ContentBackground>
        );

    return (
        <WithBackground
            active={active}
            onClose={onDismissingClose}
            preventBackgroundClick={preventBackgroundClick}
            escapeDisabled={escapeDisabled}
        >
            {dialog}
        </WithBackground>
    );
}
